"use client";

// Overlay PERMANEN untuk storefront toko yang dinonaktifkan pengelola platform.
//
// KEPUTUSAN PEMILIK (2026-08) — ini MENGGANTI kebijakan lama "tidak mengunci apa
// pun" yang tercatat di kepala `AdminSubscriptionAlert.tsx`. Sebelum ini,
// menekan **Nonaktifkan Langganan** di panel platform tidak berpengaruh sama
// sekali pada storefront: toko yang dinonaktifkan tetap melayani pesanan.
//
// PERMANEN, dan cara mencapainya penting: komponen ini TIDAK PUNYA STATE dan
// tidak punya tombol tutup. Tidak ada `useState`, tidak ada kunci
// sessionStorage, tidak ada `onClose`. Popup lain di aplikasi ini bisa ditutup
// justru KARENA mereka menyimpan penanda "sudah tampil" (idiom
// `galea-popup-shown-*`); di sini tidak ada yang bisa dihapus pembeli untuk
// melewatinya. Selama `suspended` masih true, overlay-nya kembali pada setiap
// render, setiap navigasi, setiap muat ulang.
//
// SERVER-RENDERED: `suspended` datang sebagai PROP dari root layout (yang sudah
// meresolusi toko lewat `resolveStoreContext()` — nol round-trip tambahan),
// bukan dari fetch di dalam komponen ini. Akibatnya overlay sudah ada di HTML
// pertama: tak ada jendela beberapa ratus milidetik saat storefront masih bisa
// dipakai, dan tetap tampil walau JavaScript gagal dimuat.
//
// KENAPA CLIENT COMPONENT kalau begitu: hanya untuk `usePathname()` — root
// layout membungkus SELURUH aplikasi termasuk `/admin` dan `/platform`, dan
// overlay ini tidak boleh menutupi keduanya (panel admin punya pemberitahuannya
// sendiri lewat `AdminSubscriptionAlert`, dan menutup `/admin/login` akan
// mengunci pemilik toko dari satu-satunya jalur melihat sebabnya). Pola persis
// `ConfigGuard`.
//
// BUKAN BATAS KEAMANAN. Ini tanda "TUTUP" di pintu, bukan gembok: siapa pun yang
// membuka devtools bisa menghapus elemennya, dan API di belakangnya tetap
// melayani. Penguncian yang sesungguhnya (menolak checkout di server) adalah
// pekerjaan tersendiri — kalau kelak diinginkan, tempatnya di RPC checkout,
// bukan di sini.

import { usePathname } from "next/navigation";
import { storeSuspendedCopyForBuyer } from "@/lib/storeAccess";

/**
 * Awalan rute yang TIDAK ditutupi overlay pembeli.
 *
 * `/admin` — panel toko: punya pemberitahuannya sendiri, dan `/admin/login`
 *            harus tetap terbuka supaya pemilik toko bisa masuk & membaca
 *            sebabnya.
 * `/platform` — panel pengelola platform. Di apex platform `storeId` memang
 *            null sehingga `suspended` tak pernah true, tapi menutupnya secara
 *            eksplisit membuat orang yang menonaktifkan toko mustahil ikut
 *            terkunci oleh perbuatannya sendiri.
 */
const EXEMPT_PREFIXES = ["/admin", "/platform"];

export function StoreSuspendedGate({ suspended }: { suspended: boolean }) {
  const pathname = usePathname();

  if (!suspended) return null;
  if (
    EXEMPT_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    )
  ) {
    return null;
  }

  const copy = storeSuspendedCopyForBuyer();

  return (
    <div
      // `fixed inset-0` + z tertinggi di aplikasi (popup toko memakai z-[60],
      // sheet admin z-50): pemberitahuan ini harus menang atas apa pun yang
      // kebetulan sedang terbuka saat toko dinonaktifkan.
      className="fixed inset-0 z-[999] flex items-center justify-center bg-neutral-900/70 p-5 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="store-suspended-title"
      aria-describedby="store-suspended-body"
    >
      {/* Kunci gulir lewat CSS, bukan useEffect: berlaku sejak HTML pertama
          (tanpa menunggu hidrasi) dan tetap bekerja saat JS mati — sekaligus
          menjaga komponen ini benar-benar tanpa state. */}
      <style>{"body{overflow:hidden!important}"}</style>

      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-soft">
        <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
          {/* Gembok. Inline SVG, bukan lucide: overlay ini harus tetap utuh
              walau chunk ikon gagal dimuat — persis keadaan jaringan buruk
              tempat pembeli paling mungkin salah kirim uang. */}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-7 w-7 text-neutral-500"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="10" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>

        <h1
          id="store-suspended-title"
          className="text-lg font-extrabold text-ink"
        >
          {copy.title}
        </h1>

        <p
          id="store-suspended-body"
          className="mt-2 text-sm leading-relaxed text-muted"
        >
          {copy.body}
        </p>

        {copy.hint ? (
          <p className="mt-4 rounded-xl bg-neutral-50 p-3 text-xs leading-relaxed text-neutral-500">
            {copy.hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}
